import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import WorkoutClock from './WorkoutClock';

function PersonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.5 15a4.5 4.5 0 0 1 9 0H3.5Z" />
    </svg>
  );
}

export interface BackTarget {
  label: string;
  /** Where to go. Omit and pass `onClick` for in-page steps that aren't routes. */
  to?: string;
  onClick?: () => void;
}

/**
 * Global site header, rendered on every page.
 *
 * Back navigation lives here rather than in a separate row beneath, so a page
 * carries one bar of chrome instead of two. Every control is a <button> — the
 * app uses no anchor/link text anywhere.
 *
 * On narrow screens the brand hides when a back button is present, since both
 * plus the clock and the account menu will not fit. Home stays reachable from
 * the account menu, which is why that menu lists it.
 */
export default function SiteHeader({ back }: { back?: BackTarget }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  return (
    <nav className={`app-nav${back ? ' app-nav--with-back' : ''}`}>
      <div className="app-nav-left">
        {back && (
          <button
            type="button"
            className="nav-back"
            onClick={() => (back.onClick ? back.onClick() : navigate(back.to ?? '/'))}
          >
            <span aria-hidden="true">←</span>
            <span className="nav-back-label">{back.label}</span>
          </button>
        )}
        <button type="button" className="app-nav-brand" onClick={() => navigate('/')}>
          Gym Coach
        </button>
      </div>

      <WorkoutClock />

      <div ref={userRef} style={{ position: 'relative', flexShrink: 0 }}>
        {user ? (
          <>
            <button type="button" className="user-menu-btn" onClick={() => setMenuOpen(v => !v)}>
              <PersonIcon />
              <span className="user-menu-name">{user.displayName}</span>
              <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>
                {menuOpen ? '▲' : '▼'}
              </span>
            </button>
            {menuOpen && (
              <div className="user-menu">
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={() => { setMenuOpen(false); navigate('/'); }}
                >
                  Home
                </button>
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={() => { setMenuOpen(false); navigate('/history'); }}
                >
                  History
                </button>
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                >
                  My profile
                </button>
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={() => { logout(); setMenuOpen(false); }}
                >
                  Sign out
                </button>
              </div>
            )}
          </>
        ) : (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => navigate('/login', { state: { from: location.pathname + location.search } })}
          >
            Sign In
          </button>
        )}
      </div>
    </nav>
  );
}
